import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  BellRing,
  CalendarClock,
  Check,
  ChevronDown,
  CircleOff,
  Clock3,
  Edit3,
  ExternalLink,
  Eye,
  Filter,
  Globe2,
  Image as ImageIcon,
  Link2,
  Megaphone,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import {
  Announcement,
  AnnouncementActionType,
  AnnouncementCreateRequest,
  createAnnouncement,
  deleteAnnouncement,
  listAdminAnnouncements,
  updateAnnouncement,
} from "./announcementApi";
import styles from "./AnnouncementPage.module.css";

type FormState = {
  title: string;
  description: string;
  image_url: string;
  button_text: string;
  action_type: AnnouncementActionType;
  action_value: string;
  category: string;
  priority: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  language: string;
};

type StatusFilter = "all" | "live" | "scheduled" | "expired" | "inactive";
type SortMode = "priority" | "newest" | "start";

const initialForm = (): FormState => ({
  title: "",
  description: "",
  image_url: "",
  button_text: "",
  action_type: "none",
  action_value: "",
  category: "general",
  priority: "0",
  is_active: true,
  start_date: toLocalDateTimeInput(new Date().toISOString()),
  end_date: "",
  language: "en",
});

const dateTimeFormatter = new Intl.DateTimeFormat("tr-TR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
}

function toLocalDateTimeInput(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function nullable(value: string) {
  return value.trim() || null;
}

function getLifecycle(item: Announcement): Exclude<StatusFilter, "all"> {
  if (!item.is_active) return "inactive";
  const now = Date.now();
  const start = new Date(item.start_date).getTime();
  const end = item.end_date ? new Date(item.end_date).getTime() : null;
  if (!Number.isNaN(start) && start > now) return "scheduled";
  if (end !== null && !Number.isNaN(end) && end < now) return "expired";
  return "live";
}

const statusLabels: Record<Exclude<StatusFilter, "all">, string> = {
  live: "Yayında",
  scheduled: "Planlandı",
  expired: "Süresi doldu",
  inactive: "Pasif",
};

const actionLabels: Record<AnnouncementActionType, string> = {
  none: "Aksiyon yok",
  internal_route: "Uygulama rotası",
  external_url: "Harici bağlantı",
  song: "Şarkı",
  training: "Eğitim",
};

function validateForm(form: FormState) {
  const title = form.title.trim();
  const description = form.description.trim();
  const category = form.category.trim();
  const language = form.language.trim();

  if (title.length < 2 || title.length > 100)
    return "Başlık 2–100 karakter arasında olmalıdır.";
  if (description.length < 2 || description.length > 300)
    return "Açıklama 2–300 karakter arasında olmalıdır.";
  if (category.length < 2 || category.length > 50)
    return "Kategori 2–50 karakter arasında olmalıdır.";
  if (language.length < 2 || language.length > 5)
    return "Dil kodu 2–5 karakter arasında olmalıdır.";
  if (form.button_text.trim().length > 40)
    return "Buton metni en fazla 40 karakter olabilir.";
  if (form.action_value.trim().length > 300)
    return "Aksiyon değeri en fazla 300 karakter olabilir.";
  if (form.image_url.trim()) {
    try {
      new URL(form.image_url.trim());
    } catch {
      return "Görsel adresi geçerli bir URL olmalıdır.";
    }
  }
  if (form.action_type === "external_url" && form.action_value.trim()) {
    try {
      new URL(form.action_value.trim());
    } catch {
      return "Harici aksiyon için geçerli bir URL girin.";
    }
  }
  if (form.action_type !== "none" && !form.action_value.trim())
    return "Seçilen aksiyon için bir aksiyon değeri girin.";
  if (!form.start_date) return "Başlangıç tarihi zorunludur.";

  const start = new Date(form.start_date).getTime();
  const end = form.end_date ? new Date(form.end_date).getTime() : null;
  if (Number.isNaN(start)) return "Başlangıç tarihi geçersiz.";
  if (end !== null && Number.isNaN(end)) return "Bitiş tarihi geçersiz.";
  if (end !== null && end < start)
    return "Bitiş tarihi başlangıç tarihinden önce olamaz.";
  if (!Number.isFinite(Number(form.priority)))
    return "Öncelik geçerli bir sayı olmalıdır.";
  return null;
}

function formToPayload(form: FormState): AnnouncementCreateRequest {
  const noAction = form.action_type === "none";
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    image_url: nullable(form.image_url),
    button_text: noAction ? null : nullable(form.button_text),
    action_type: form.action_type,
    action_value: noAction ? null : nullable(form.action_value),
    category: form.category.trim(),
    priority: Number(form.priority),
    is_active: form.is_active,
    start_date: new Date(form.start_date).toISOString(),
    end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
    language: form.language.trim().toLowerCase(),
  };
}

function itemToForm(item: Announcement): FormState {
  return {
    title: item.title || "",
    description: item.description || "",
    image_url: item.image_url || "",
    button_text: item.button_text || "",
    action_type: item.action_type || "none",
    action_value: item.action_value || "",
    category: item.category || "general",
    priority: String(item.priority ?? 0),
    is_active: item.is_active !== false,
    start_date: toLocalDateTimeInput(item.start_date),
    end_date: toLocalDateTimeInput(item.end_date),
    language: item.language || "en",
  };
}

export default function AnnouncementPage() {
  const access = getCurrentTokenAccessInfo();
  const canWrite = Boolean(access?.hasPanelAccess);
  const canDelete = Boolean(access?.canDeleteAnnouncements);

  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState<FormState>(initialForm);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [sortMode, setSortMode] = useState<SortMode>("priority");
  const [sortAscending, setSortAscending] = useState(false);

  const loadItems = useCallback(async (refresh = false) => {
    try {
      refresh ? setRefreshing(true) : setLoading(true);
      setError("");
      const data = await listAdminAnnouncements();
      setItems(data);
    } catch (err: any) {
      setError(err?.message || "Duyurular yüklenemedi.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const languages = useMemo(
    () =>
      Array.from(
        new Set(items.map((item) => item.language).filter(Boolean))
      ).sort(),
    [items]
  );

  const counts = useMemo(
    () =>
      items.reduce(
        (result, item) => {
          result[getLifecycle(item)] += 1;
          return result;
        },
        { live: 0, scheduled: 0, expired: 0, inactive: 0 }
      ),
    [items]
  );

  const visibleItems = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    const filtered = items.filter((item) => {
      if (statusFilter !== "all" && getLifecycle(item) !== statusFilter)
        return false;
      if (languageFilter !== "all" && item.language !== languageFilter)
        return false;
      if (
        query &&
        ![
          item.title,
          item.description,
          item.category,
          item.action_value,
          item.id,
        ].some((value) =>
          String(value || "")
            .toLocaleLowerCase("tr-TR")
            .includes(query)
        )
      )
        return false;
      return true;
    });

    return filtered.sort((a, b) => {
      const direction = sortAscending ? 1 : -1;
      if (sortMode === "priority")
        return (a.priority - b.priority) * direction;
      const aValue = new Date(
        sortMode === "newest" ? a.created_at : a.start_date
      ).getTime();
      const bValue = new Date(
        sortMode === "newest" ? b.created_at : b.start_date
      ).getTime();
      return (aValue - bValue) * direction;
    });
  }, [items, languageFilter, search, sortAscending, sortMode, statusFilter]);

  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K]
  ) => setForm((current) => ({ ...current, [field]: value }));

  const openCreate = () => {
    setForm(initialForm());
    setEditingId(null);
    setEditorOpen(true);
    setError("");
    setMessage("");
  };

  const openEdit = (item: Announcement) => {
    setForm(itemToForm(item));
    setEditingId(item.id);
    setEditorOpen(true);
    setError("");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    setForm(initialForm());
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canWrite) return;
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setMessage("");
      const payload = formToPayload(form);
      if (editingId) {
        const updated = await updateAnnouncement(editingId, payload);
        setItems((current) =>
          current.map((item) => (item.id === editingId ? updated : item))
        );
        setMessage("Duyuru başarıyla güncellendi.");
      } else {
        const created = await createAnnouncement(payload);
        setItems((current) => [created, ...current]);
        setMessage("Yeni duyuru başarıyla oluşturuldu.");
      }
      closeEditor();
      await loadItems(true);
    } catch (err: any) {
      setError(err?.message || "Duyuru kaydedilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !canDelete) return;
    try {
      setDeleting(true);
      setError("");
      await deleteAnnouncement(deleteTarget.id);
      setItems((current) =>
        current.filter((item) => item.id !== deleteTarget.id)
      );
      setMessage("Duyuru silindi.");
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err?.message || "Duyuru silinemedi.");
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setLanguageFilter("all");
  };

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroCopy}>
          <span className={styles.eyebrow}>
            <Megaphone size={14} /> İletişim merkezi
          </span>
          <h1>Duyurular</h1>
          <p>
            Uygulama içi duyuruları planlayın, hedef aksiyonlarını yönetin ve
            yayın durumunu tek ekrandan takip edin.
          </p>
        </div>
        <div className={styles.heroActions}>
          <button
            type="button"
            className={styles.refreshButton}
            disabled={refreshing}
            onClick={() => loadItems(true)}
          >
            <RefreshCw size={16} className={refreshing ? styles.spinning : ""} />
            Yenile
          </button>
          {canWrite && (
            <button type="button" className={styles.createButton} onClick={openCreate}>
              <Plus size={17} /> Yeni duyuru
            </button>
          )}
        </div>
      </header>

      <section className={styles.statsGrid}>
        <article>
          <span className={styles.statIconBlue}><Megaphone size={18} /></span>
          <div><strong>{items.length}</strong><small>Toplam duyuru</small></div>
        </article>
        <article>
          <span className={styles.statIconGreen}><BellRing size={18} /></span>
          <div><strong>{counts.live}</strong><small>Şu an yayında</small></div>
        </article>
        <article>
          <span className={styles.statIconViolet}><CalendarClock size={18} /></span>
          <div><strong>{counts.scheduled}</strong><small>Planlanan</small></div>
        </article>
        <article>
          <span className={styles.statIconAmber}><CircleOff size={18} /></span>
          <div><strong>{counts.inactive + counts.expired}</strong><small>Pasif / sona eren</small></div>
        </article>
      </section>

      {(error || message) && (
        <div className={`${styles.feedback} ${error ? styles.feedbackError : styles.feedbackSuccess}`}>
          {error ? <AlertCircle size={17} /> : <Check size={17} />}
          <span>{error || message}</span>
          <button
            type="button"
            aria-label="Mesajı kapat"
            onClick={() => { setError(""); setMessage(""); }}
          >
            <X size={15} />
          </button>
        </div>
      )}

      {editorOpen && (
        <section className={styles.editor}>
          <div className={styles.sectionHeader}>
            <div>
              <span>{editingId ? "Duyuruyu düzenle" : "Yeni yayın"}</span>
              <h2>{editingId ? form.title || "Duyuru" : "Duyuru oluştur"}</h2>
            </div>
            <button type="button" aria-label="Editörü kapat" onClick={closeEditor}>
              <X size={18} />
            </button>
          </div>

          <form onSubmit={submit}>
            <div className={styles.formMain}>
              <label className={styles.fullField}>
                <span>Başlık <em>*</em><small>{form.title.length}/100</small></span>
                <input
                  autoFocus
                  value={form.title}
                  minLength={2}
                  maxLength={100}
                  onChange={(event) => updateField("title", event.target.value)}
                  placeholder="Yeni özellikler yayında"
                  required
                />
              </label>
              <label className={styles.fullField}>
                <span>Açıklama <em>*</em><small>{form.description.length}/300</small></span>
                <textarea
                  value={form.description}
                  minLength={2}
                  maxLength={300}
                  rows={4}
                  onChange={(event) => updateField("description", event.target.value)}
                  placeholder="Kullanıcıların göreceği duyuru metni..."
                  required
                />
              </label>
              <label>
                <span><ImageIcon size={13} /> Görsel URL</span>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(event) => updateField("image_url", event.target.value)}
                  placeholder="https://..."
                />
              </label>
              <label>
                <span>Kategori <em>*</em></span>
                <input
                  value={form.category}
                  minLength={2}
                  maxLength={50}
                  onChange={(event) => updateField("category", event.target.value)}
                  placeholder="general"
                  required
                />
              </label>
              <label>
                <span><CalendarClock size={13} /> Başlangıç <em>*</em></span>
                <input
                  type="datetime-local"
                  value={form.start_date}
                  onChange={(event) => updateField("start_date", event.target.value)}
                  required
                />
              </label>
              <label>
                <span><Clock3 size={13} /> Bitiş</span>
                <input
                  type="datetime-local"
                  value={form.end_date}
                  min={form.start_date || undefined}
                  onChange={(event) => updateField("end_date", event.target.value)}
                />
              </label>
              <label>
                <span><Globe2 size={13} /> Dil kodu <em>*</em></span>
                <input
                  value={form.language}
                  minLength={2}
                  maxLength={5}
                  onChange={(event) => updateField("language", event.target.value)}
                  placeholder="en"
                  required
                />
              </label>
              <label>
                <span>Öncelik</span>
                <input
                  type="number"
                  step="1"
                  value={form.priority}
                  onChange={(event) => updateField("priority", event.target.value)}
                />
              </label>
            </div>

            <div className={styles.actionBuilder}>
              <div>
                <span><Link2 size={14} /> Etkileşim</span>
                <p>Duyuruya buton ve hedef aksiyon bağlayın.</p>
              </div>
              <label>
                <span>Aksiyon tipi</span>
                <select
                  value={form.action_type}
                  onChange={(event) =>
                    updateField("action_type", event.target.value as AnnouncementActionType)
                  }
                >
                  {Object.entries(actionLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Buton metni</span>
                <input
                  value={form.button_text}
                  maxLength={40}
                  disabled={form.action_type === "none"}
                  onChange={(event) => updateField("button_text", event.target.value)}
                  placeholder="Şimdi keşfet"
                />
              </label>
              <label className={styles.fullField}>
                <span>Aksiyon değeri</span>
                <input
                  value={form.action_value}
                  maxLength={300}
                  disabled={form.action_type === "none"}
                  onChange={(event) => updateField("action_value", event.target.value)}
                  placeholder={
                    form.action_type === "external_url"
                      ? "https://..."
                      : form.action_type === "song"
                        ? "Şarkı ID"
                        : "Rota veya içerik ID"
                  }
                />
              </label>
            </div>

            <div className={styles.editorFooter}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) => updateField("is_active", event.target.checked)}
                />
                <span><i /></span>
                <div>
                  <strong>Yayın aktif</strong>
                  <small>Tarih aralığı geldiğinde kullanıcılar görebilir.</small>
                </div>
              </label>
              <div>
                <button type="button" onClick={closeEditor} disabled={submitting}>
                  İptal
                </button>
                <button type="submit" disabled={submitting || !canWrite}>
                  {editingId ? <Save size={16} /> : <Send size={16} />}
                  {submitting
                    ? "Kaydediliyor"
                    : editingId
                      ? "Değişiklikleri kaydet"
                      : "Duyuruyu oluştur"}
                </button>
              </div>
            </div>
          </form>
        </section>
      )}

      <section className={styles.directory}>
        <div className={styles.directoryHeader}>
          <div>
            <span>Yayın arşivi</span>
            <h2>Duyuru listesi</h2>
          </div>
          <div className={styles.permissionBadge}>
            <ShieldCheck size={14} />
            {canDelete ? "ADMIN" : canWrite ? "CONTENT MANAGER" : "SALT OKUNUR"}
          </div>
        </div>

        <div className={styles.toolbar}>
          <label className={styles.searchBox}>
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Başlık, açıklama, kategori veya ID ara..."
            />
            {search && (
              <button type="button" aria-label="Aramayı temizle" onClick={() => setSearch("")}>
                <X size={14} />
              </button>
            )}
          </label>
          <label>
            <Filter size={14} />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
              <option value="all">Tüm durumlar</option>
              <option value="live">Yayında</option>
              <option value="scheduled">Planlandı</option>
              <option value="expired">Süresi doldu</option>
              <option value="inactive">Pasif</option>
            </select>
          </label>
          <label>
            <Globe2 size={14} />
            <select value={languageFilter} onChange={(event) => setLanguageFilter(event.target.value)}>
              <option value="all">Tüm diller</option>
              {languages.map((language) => (
                <option key={language} value={language}>{language.toUpperCase()}</option>
              ))}
            </select>
          </label>
          <label>
            <Sparkles size={14} />
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value as SortMode)}>
              <option value="priority">Öncelik</option>
              <option value="newest">Oluşturulma</option>
              <option value="start">Başlangıç</option>
            </select>
          </label>
          <button
            type="button"
            className={styles.sortDirection}
            aria-label="Sıralama yönünü değiştir"
            onClick={() => setSortAscending((current) => !current)}
          >
            {sortAscending ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          </button>
        </div>

        {(search || statusFilter !== "all" || languageFilter !== "all") && (
          <div className={styles.resultLine}>
            <span>{visibleItems.length} sonuç gösteriliyor</span>
            <button type="button" onClick={resetFilters}>Filtreleri temizle</button>
          </div>
        )}

        {loading ? (
          <div className={styles.state}>
            <div className={styles.loader} />
            <h3>Duyurular hazırlanıyor</h3>
            <p>Yayın planları ve aksiyonlar yükleniyor.</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className={styles.state}>
            <Megaphone size={32} />
            <h3>{items.length ? "Filtrelere uygun duyuru yok" : "Henüz duyuru yok"}</h3>
            <p>{items.length ? "Arama veya filtreleri değiştirin." : "İlk uygulama içi duyurunuzu oluşturun."}</p>
            {items.length === 0 && canWrite && (
              <button type="button" onClick={openCreate}><Plus size={16} /> Duyuru oluştur</button>
            )}
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {visibleItems.map((item) => {
              const lifecycle = getLifecycle(item);
              const expanded = expandedId === item.id;
              return (
                <article key={item.id} className={styles.card}>
                  <div className={styles.cardVisual}>
                    {item.image_url ? (
                      <img src={item.image_url} alt="" loading="lazy" />
                    ) : (
                      <span><Megaphone size={28} /></span>
                    )}
                    <div className={`${styles.lifecycle} ${styles[`lifecycle_${lifecycle}`]}`}>
                      <i /> {statusLabels[lifecycle]}
                    </div>
                    <span className={styles.priority}>P{item.priority}</span>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.cardMeta}>
                      <span>{item.category}</span>
                      <span><Globe2 size={12} /> {item.language.toUpperCase()}</span>
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className={styles.schedule}>
                      <span><CalendarClock size={14} /><i>Başlangıç</i><strong>{formatDateTime(item.start_date)}</strong></span>
                      <span><Clock3 size={14} /><i>Bitiş</i><strong>{formatDateTime(item.end_date)}</strong></span>
                    </div>
                    {item.action_type !== "none" && (
                      <div className={styles.cardAction}>
                        {item.action_type === "external_url" ? <ExternalLink size={14} /> : <Link2 size={14} />}
                        <div>
                          <span>{item.button_text || actionLabels[item.action_type]}</span>
                          <strong>{item.action_value || "—"}</strong>
                        </div>
                      </div>
                    )}
                    {expanded && (
                      <div className={styles.cardDetails}>
                        <div><span>ID</span><strong>{item.id}</strong></div>
                        <div><span>Aksiyon tipi</span><strong>{actionLabels[item.action_type]}</strong></div>
                        <div><span>Aktif</span><strong>{item.is_active ? "Evet" : "Hayır"}</strong></div>
                        <div><span>Oluşturuldu</span><strong>{formatDateTime(item.created_at)}</strong></div>
                        <div><span>Güncellendi</span><strong>{formatDateTime(item.updated_at)}</strong></div>
                        <div><span>Görsel URL</span><strong>{item.image_url || "—"}</strong></div>
                      </div>
                    )}
                  </div>
                  <footer className={styles.cardFooter}>
                    <button type="button" onClick={() => setExpandedId(expanded ? null : item.id)}>
                      <Eye size={15} /> {expanded ? "Detayları kapat" : "Tüm detaylar"} <ChevronDown size={14} />
                    </button>
                    <div>
                      {canWrite && (
                        <button type="button" onClick={() => openEdit(item)} aria-label="Düzenle">
                          <Edit3 size={15} />
                        </button>
                      )}
                      {canDelete && (
                        <button type="button" className={styles.deleteButton} onClick={() => setDeleteTarget(item)} aria-label="Sil">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {deleteTarget && (
        <div className={styles.modalOverlay} role="presentation" onMouseDown={() => !deleting && setDeleteTarget(null)}>
          <section role="dialog" aria-modal="true" aria-label="Duyuruyu sil" onMouseDown={(event) => event.stopPropagation()}>
            <span className={styles.modalIcon}><Trash2 size={22} /></span>
            <h2>Duyuru silinsin mi?</h2>
            <p><strong>{deleteTarget.title}</strong> kalıcı olarak silinecek. Bu işlem geri alınamaz.</p>
            <div>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>Vazgeç</button>
              <button type="button" onClick={confirmDelete} disabled={deleting}>
                <Trash2 size={15} /> {deleting ? "Siliniyor" : "Duyuruyu sil"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
